import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Avatar,
  Grid,
  Divider,
  CircularProgress,
  Alert,
  Chip,
} from "@mui/material";
import { Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import adminService from "../../../services/admin.service";
import { IAdmin } from "../../../types/admin.types";
import { logger } from "../../../utils/logger";

export default function Profile() {
  const { t } = useTranslation();
  const [admin, setAdmin] = useState<IAdmin | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      setError(null);

      const storedAdmin = localStorage.getItem("adminData");
      if (storedAdmin) {
        const adminData: IAdmin = JSON.parse(storedAdmin);
        setAdmin(adminData);
        setFormData({
          firstName: adminData.firstName || "",
          lastName: adminData.lastName || "",
          email: adminData.email || "",
          phone: adminData.phone || "",
        });
      }

      const response: IAdmin = await adminService.getProfile();
      setAdmin(response);
      setFormData({
        firstName: response.firstName || "",
        lastName: response.lastName || "",
        email: response.email || "",
        phone: response.phone || "",
      });

      localStorage.setItem("adminData", JSON.stringify(response));
    } catch (err: any) {
      const errorMessage = err.message || "Failed to load profile";
      setError(errorMessage);
      logger.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    if (admin) {
      setFormData({
        firstName: admin.firstName || "",
        lastName: admin.lastName || "",
        email: admin.email || "",
        phone: admin.phone || "",
      });
    }
  };

  const handleSave = async () => {
    if (!admin) return;

    try {
      setLoading(true);
      const response: IAdmin = await adminService.updateAdmin(admin._id, formData);
      setAdmin(response);
      localStorage.setItem("adminData", JSON.stringify(response));
      setIsEditing(false);
      logger.info("Profile updated successfully");
    } catch (err: any) {
      const errorMessage = err.message || "Failed to update profile";
      setError(errorMessage);
      logger.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !admin) {
    return (
      <Box className="flex justify-center items-center" sx={{ height: "400px" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !admin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!admin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">{t("profile.noProfileData")}</Alert>
      </Box>
    );
  }

  const adminInitial = admin.firstName?.[0]?.toUpperCase() || "A";

  const getRoleLabel = () => admin.role?.label ?? admin.role?.name ?? "Unknown";

  const getRoleDetailLabel = () => {
    if (admin.role?.name === "super_admin") return t("profile.superAdministrator");
    return t("profile.administrator");
  };

  return (
    <Box className="w-full p-4">
      <Typography variant="h4" gutterBottom fontWeight="bold">
        {t("profile.title")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ p: 3, textAlign: "center" }}>
            <Avatar
              sx={{
                width: 120,
                height: 120,
                fontSize: 48,
                bgcolor: "#1976d2",
                margin: "0 auto 16px",
                fontWeight: "bold",
              }}
            >
              {adminInitial}
            </Avatar>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              {admin.firstName} {admin.lastName}
            </Typography>
            <Typography color="text.secondary" gutterBottom>
              {admin.email}
            </Typography>
            <Chip
              label={getRoleLabel()}
              color={admin.role?.name === "super_admin" ? "error" : "primary"}
              sx={{ mt: 1 }}
            />
            <Chip
              label={admin.active ? t("profile.active") : t("profile.inactive")}
              color={admin.active ? "success" : "error"}
              sx={{ mt: 1, ml: 1 }}
            />
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 8 }}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
              <Typography variant="h6" fontWeight="bold">
                {t("profile.personalInfo")}
              </Typography>
              {!isEditing ? (
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={handleEdit}
                >
                  {t("profile.editProfile")}
                </Button>
              ) : (
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={handleCancel}
                    color="error"
                  >
                    {t("common.cancel")}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={loading}
                  >
                    {loading ? t("profile.saving") : t("common.save")}
                  </Button>
                </Box>
              )}
            </Box>

            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t("common.firstName")}
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  variant={isEditing ? "outlined" : "filled"}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t("common.lastName")}
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  variant={isEditing ? "outlined" : "filled"}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t("profile.email")}
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  variant={isEditing ? "outlined" : "filled"}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t("profile.phone")}
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  variant={isEditing ? "outlined" : "filled"}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" fontWeight="bold" gutterBottom>
              {t("profile.accountInfo")}
            </Typography>

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  {t("profile.roleLabel")}
                </Typography>
                <Typography variant="body1" fontWeight="500">
                  {getRoleDetailLabel()}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  {t("profile.accountStatus")}
                </Typography>
                <Typography variant="body1" fontWeight="500">
                  {admin.active ? t("profile.active") : t("profile.inactive")}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  {t("profile.memberSince")}
                </Typography>
                <Typography variant="body1" fontWeight="500">
                  {new Date(admin.createdAt).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  {t("profile.permissions")}
                </Typography>
                <Typography variant="body1" fontWeight="500">
                  {t("profile.permissionsCount", { count: admin.role?.permissions?.length || 0 })}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
